import { PAYMENT_BASES, type PaymentBasis } from '@epc/contracts';
import { Plus, Trash2 } from 'lucide-react';
import { tenge } from '../../lib/format';
import { Button, IconButton } from '../../ui/button';
import { Input, Select } from '../../ui/form';

/* ───────────────────────── Графики платежей и поставок ───────────────────────── */

export interface ScheduleDraftRow {
  /** Сохранённая строка: id уходит обратно, и заявки на оплату остаются связаны с ней (ФН-02) */
  id?: string;
  kind: 'payment' | 'delivery';
  plannedDate: string;
  basis: PaymentBasis | '';
  days: string;
  percent: string;
  quantity: string;
  /** Позиция спецификации строки «поставка»: строка закрывается полученным по ней на склад */
  contractItemId: string;
  description: string;
}

const dec = (s: string) => Number(s.replace(/\s/g, '').replace(',', '.'));

export function toScheduleDraft(
  list: Array<{
    id?: string;
    kind: 'payment' | 'delivery';
    plannedDate?: string | null;
    basis?: PaymentBasis | null;
    days?: number | null;
    percent?: number | null;
    quantity?: number | null;
    contractItemId?: string | null;
    description?: string | null;
  }>,
): ScheduleDraftRow[] {
  return list.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    kind: r.kind,
    plannedDate: r.plannedDate ?? '',
    basis: (r.basis ?? '') as PaymentBasis | '',
    days: r.days?.toString() ?? '',
    percent: r.percent?.toString() ?? '',
    quantity: r.quantity?.toString() ?? '',
    contractItemId: r.contractItemId ?? '',
    description: r.description ?? '',
  }));
}

export function fromScheduleDraft(rows: ScheduleDraftRow[]) {
  return rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    kind: r.kind,
    plannedDate: r.plannedDate || null,
    basis: r.basis || null,
    days: r.days ? Number(r.days) : null,
    percent: r.percent ? dec(r.percent) : null,
    quantity: r.quantity ? dec(r.quantity) : null,
    contractItemId: r.kind === 'delivery' && r.contractItemId ? r.contractItemId : null,
    description: r.description || null,
  }));
}

export function ScheduleRowsEditor({
  rows,
  onChange,
  kinds,
  items = [],
}: {
  rows: ScheduleDraftRow[];
  onChange: (rows: ScheduleDraftRow[]) => void;
  /** Позиции спецификации договора — для привязки строки поставки */
  items?: Array<{ id: string; name: string; unit: string }>;
  /** Какие строки можно добавлять и выбирать: без доступа к суммам — только поставки */
  kinds?: Array<'payment' | 'delivery'>;
}) {
  const only = kinds?.length === 1 ? kinds[0]! : null;
  const upd = (i: number, k: keyof ScheduleDraftRow, v: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: строки графика без id до сохранения
          key={i}
          className="grid grid-cols-2 gap-2 border-b border-line pb-2 last:border-b-0 md:grid-cols-[120px_150px_minmax(0,1fr)_80px_80px_auto]"
        >
          <Select
            className="max-md:h-11"
            aria-label="Вид строки"
            value={r.kind}
            disabled={Boolean(only)}
            onChange={(e) => upd(i, 'kind', e.target.value)}
          >
            {!only || only === 'payment' ? <option value="payment">Платёж</option> : null}
            {!only || only === 'delivery' ? <option value="delivery">Поставка</option> : null}
          </Select>
          <Input
            className="max-md:h-11"
            aria-label="Плановая дата"
            type="date"
            value={r.plannedDate}
            onChange={(e) => upd(i, 'plannedDate', e.target.value)}
          />
          <Select
            className="max-md:h-11"
            aria-label="Основание платежа"
            value={r.basis}
            onChange={(e) => upd(i, 'basis', e.target.value)}
            disabled={r.kind === 'delivery'}
          >
            <option value="">—</option>
            {Object.entries(PAYMENT_BASES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
          <Input
            className="max-md:h-11"
            aria-label={r.kind === 'payment' ? 'Процент от суммы' : 'Количество'}
            placeholder={r.kind === 'payment' ? '%' : 'кол-во'}
            value={r.kind === 'payment' ? r.percent : r.quantity}
            onChange={(e) => upd(i, r.kind === 'payment' ? 'percent' : 'quantity', e.target.value)}
          />
          <Input
            className="max-md:h-11"
            aria-label="Дней после накладной"
            placeholder="дней"
            value={r.days}
            disabled={r.basis !== 'invoice_plus_days'}
            onChange={(e) => upd(i, 'days', e.target.value)}
          />
          <IconButton label="Убрать строку" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <Trash2 className="size-4" />
          </IconButton>
          {r.kind === 'delivery' && items.length ? (
            <Select
              aria-label="Позиция спецификации"
              className="col-span-2 max-md:h-11 md:col-span-5"
              value={r.contractItemId}
              onChange={(e) => upd(i, 'contractItemId', e.target.value)}
            >
              <option value="">Позиция не выбрана — строка закрывается по доле всей спецификации</option>
              {items.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name}, {it.unit}
                </option>
              ))}
            </Select>
          ) : null}
          <Input
            aria-label="Описание"
            className="col-span-2 max-md:h-11 md:col-span-5"
            placeholder="описание: что поставляется, за что платёж"
            value={r.description}
            onChange={(e) => upd(i, 'description', e.target.value)}
          />
        </div>
      ))}
      <div>
        <Button
          size="sm"
          variant="secondary"
          icon={<Plus className="size-4" />}
          onClick={() =>
            onChange([
              ...rows,
              {
                kind: only ?? 'payment',
                plannedDate: '',
                basis: only === 'delivery' ? '' : 'advance',
                days: '',
                percent: '',
                quantity: '',
                contractItemId: '',
                description: '',
              },
            ])
          }
        >
          Строка
        </Button>
      </div>
    </div>
  );
}

/* ───────────────────────── Спецификация ───────────────────────── */

export interface ItemDraftRow {
  name: string;
  unit: string;
  quantity: string;
  price: string;
  requestLineId: string | null;
  budgetLineId: string | null;
}

export function toItemsDraft(
  list: Array<{
    name: string;
    unit: string;
    quantity: number;
    price: number | null;
    requestLineId?: string | null;
    budgetLineId?: string | null;
  }>,
): ItemDraftRow[] {
  return list.map((i) => ({
    name: i.name,
    unit: i.unit,
    quantity: String(i.quantity),
    price: i.price === null ? '' : String(i.price),
    requestLineId: i.requestLineId ?? null,
    budgetLineId: i.budgetLineId ?? null,
  }));
}

export function fromItemsDraft(rows: ItemDraftRow[]) {
  return rows.map((r) => ({
    name: r.name.trim(),
    unit: r.unit.trim(),
    quantity: dec(r.quantity),
    price: dec(r.price),
    requestLineId: r.requestLineId,
    budgetLineId: r.budgetLineId,
  }));
}

export function itemsDraftTotal(rows: ItemDraftRow[]): number {
  return rows.reduce((s, r) => s + (dec(r.quantity) || 0) * (dec(r.price) || 0), 0);
}

export function itemsDraftValid(rows: ItemDraftRow[]): boolean {
  return (
    rows.length > 0 &&
    rows.every(
      (r) =>
        r.name.trim().length >= 2 &&
        r.unit.trim().length >= 1 &&
        dec(r.quantity) > 0 &&
        Number.isFinite(dec(r.price)) &&
        dec(r.price) >= 0,
    )
  );
}

export function ItemsRowsEditor({
  rows,
  onChange,
}: {
  rows: ItemDraftRow[];
  onChange: (rows: ItemDraftRow[]) => void;
}) {
  const upd = (i: number, k: keyof ItemDraftRow, v: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: новые позиции без id до подписания
        <div key={i} className="grid grid-cols-[1fr_auto] gap-2 md:grid-cols-[1fr_80px_100px_130px_auto]">
          <Input
            aria-label="Позиция"
            className="col-span-2 md:col-span-1"
            placeholder="позиция"
            value={r.name}
            onChange={(e) => upd(i, 'name', e.target.value)}
          />
          <Input
            aria-label="Единица"
            placeholder="ед."
            value={r.unit}
            onChange={(e) => upd(i, 'unit', e.target.value)}
          />
          <Input
            aria-label="Количество"
            inputMode="decimal"
            placeholder="кол-во"
            value={r.quantity}
            onChange={(e) => upd(i, 'quantity', e.target.value)}
          />
          <Input
            aria-label="Цена, ₸"
            inputMode="decimal"
            placeholder="цена, ₸"
            value={r.price}
            onChange={(e) => upd(i, 'price', e.target.value)}
          />
          <IconButton label="Убрать позицию" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          size="sm"
          variant="secondary"
          icon={<Plus className="size-4" />}
          onClick={() =>
            onChange([
              ...rows,
              { name: '', unit: '', quantity: '', price: '', requestLineId: null, budgetLineId: null },
            ])
          }
        >
          Позиция
        </Button>
        <span className="num text-[14px] text-ink">Итого {tenge(itemsDraftTotal(rows))}</span>
      </div>
    </div>
  );
}
